import { useMemo } from 'react'
import { Users, Building2, CreditCard, Activity } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/Card'
import { useBusinesses, useUsers } from '@/hooks/useAdminData'
import { Badge } from '@/components/ui/Badge'
import { useTranslation } from 'react-i18next'
import { useLocaleFormat } from '@/hooks/useLocaleFormat'
import { enumLabel } from '@/lib/enumLabel'
import { byCreatedAtDesc } from '@/lib/normalize'

export function DashboardPage() {
  const { t } = useTranslation()
  const { formatDate } = useLocaleFormat()
  const { data: users, isLoading: usersLoading } = useUsers()
  const { data: businesses, isLoading: businessesLoading } = useBusinesses()

  const metrics = useMemo(() => {
    if (!users || !businesses) return null

    const activeUsers = users.filter((u) => u.isActive).length
    const activeSubscriptions = businesses.filter(
      (b) => b.subscription?.status === 'active'
    ).length

    return {
      totalUsers: users.length,
      activeUsers,
      totalBusinesses: businesses.length,
      activeSubscriptions,
    }
  }, [users, businesses])

  const recentUsers = useMemo(() => {
    if (!users) return []
    return [...users].sort(byCreatedAtDesc).slice(0, 5)
  }, [users])

  const recentBusinesses = useMemo(() => {
    if (!businesses) return []
    return [...businesses].sort(byCreatedAtDesc).slice(0, 5)
  }, [businesses])

  const loading = usersLoading || businessesLoading

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-500 border-t-transparent" />
      </div>
    )
  }

  const metricCards = [
    {
      label: t('dashboard.totalUsers'),
      value: metrics?.totalUsers ?? 0,
      icon: Users,
      color: 'text-blue-600 bg-blue-100',
    },
    {
      label: t('dashboard.activeUsers'),
      value: metrics?.activeUsers ?? 0,
      icon: Activity,
      color: 'text-green-600 bg-green-100',
    },
    {
      label: t('dashboard.businesses'),
      value: metrics?.totalBusinesses ?? 0,
      icon: Building2,
      color: 'text-purple-600 bg-purple-100',
    },
    {
      label: t('dashboard.activeSubscriptions'),
      value: metrics?.activeSubscriptions ?? 0,
      icon: CreditCard,
      color: 'text-amber-600 bg-amber-100',
    },
  ]

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {metricCards.map((card) => (
          <Card key={card.label}>
            <CardContent className="flex items-center gap-4">
              <div className={`rounded-lg p-3 ${card.color}`}>
                <card.icon className="h-6 w-6" />
              </div>
              <div>
                <p className="text-sm text-gray-500">{card.label}</p>
                <p className="text-2xl font-bold text-gray-900">{card.value}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card>
          <div className="border-b border-gray-200 px-6 py-4">
            <h3 className="font-semibold text-gray-900">{t('dashboard.recentUsers')}</h3>
          </div>
          <CardContent className="p-0">
            {recentUsers.length === 0 ? (
              <p className="px-6 py-4 text-sm text-gray-500">{t('dashboard.noUsers')}</p>
            ) : (
              <ul className="divide-y divide-gray-100">
                {recentUsers.map((user) => (
                  <li key={user.id} className="flex items-center justify-between gap-3 px-4 py-3 sm:px-6">
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {user.displayName}
                      </p>
                      <p className="text-xs text-gray-500">{user.email}</p>
                    </div>
                    <Badge
                      variant={
                        user.role === 'admin'
                          ? 'info'
                          : user.role === 'caregiver'
                          ? 'success'
                          : 'default'
                      }
                    >
                      {enumLabel(t, 'roles', user.role)}
                    </Badge>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        <Card>
          <div className="border-b border-gray-200 px-6 py-4">
            <h3 className="font-semibold text-gray-900">{t('dashboard.recentBusinesses')}</h3>
          </div>
          <CardContent className="p-0">
            {recentBusinesses.length === 0 ? (
              <p className="px-6 py-4 text-sm text-gray-500">{t('dashboard.noBusinesses')}</p>
            ) : (
              <ul className="divide-y divide-gray-100">
                {recentBusinesses.map((biz) => (
                  <li key={biz.id} className="flex items-center justify-between gap-3 px-4 py-3 sm:px-6">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{biz.name}</p>
                      <p className="text-xs text-gray-500">
                        {biz.createdAt ? formatDate(biz.createdAt.toDate()) : ''}
                      </p>
                    </div>
                    <Badge
                      variant={
                        biz.subscription?.status === 'active'
                          ? 'success'
                          : biz.subscription?.status === 'trialing'
                          ? 'warning'
                          : 'default'
                      }
                    >
                      {enumLabel(t, 'subscriptionStatus', biz.subscription?.status ?? 'none')}
                    </Badge>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
